import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import RealtorInviteSection from './sections/RealtorInviteSection';
import styled from 'styled-components';
import { IoChevronUp, IoChevronDown, IoChevronForward, IoMailOutline, IoChatbubbleEllipsesOutline, IoQrCodeOutline, IoClose } from 'react-icons/io5';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import InfoTooltip from '../../../components/common/InfoTooltip';
import { getNotices } from '../../../services/noticeService';
import { useAuth } from '../../../context/AuthContext';
import { getRequestCountsByStatus } from '../../../services/serviceRequestService';
import { requestShare } from '../../../bridge/webBridge';
import { getRealtorTemplate, getHqDefaultTemplate, saveRealtorTemplate, renderTemplate, FALLBACK_TEMPLATE } from '../../../services/inviteMessageService';
import { getReferralStats } from '../../../services/referralStatsService';

const SEND_SMS_URL = 'https://us-central1-ieum-8fd99.cloudfunctions.net/sendSms';

const DEFAULT_CUSTOMER_STATS = [
  { label: '접수', key: 'unread', value: 0 },
  { label: '진행중', key: 'consulting', value: 0 },
  { label: '보류', key: 'pending', value: 0 },
  { label: '취소', key: 'cancelled', value: 0 },
  { label: '완료', key: 'completed', value: 0 },
];

const DEFAULT_PARTNER_STATS = [
  { label: '발송', value: 0 },
  { label: '가입', value: 0 },
  { label: '만료예정', sub: '(6개월 이내)', value: 0 },
  { label: '완료', value: 0 },
];

const DUMMY_SETTLEMENT = {
  items: [
    { label: '마케팅 리워드', value: 0 },
    { label: '추천 수익금', value: 0 },
    { label: '출금 가능', value: 0, hasBtn: true },
    { label: '출금 완료', value: 0 },
  ],
};

const DUMMY_NOTICES = [
  { id: 1, tag: '공지', title: '이음 플렛폼 이용 안내', date: '2026-03-25' },
  { id: 2, tag: '공지', title: '이음 플렛폼 이용 안내', date: '2026-03-25' },
  { id: 3, tag: '공지', title: '이음 플렛폼 이용 안내', date: '2026-03-25' },
];

const buildFormUrl = (uid) => `https://ieum-landing.web.app/apply?ref=${uid || 'demo'}`;

const formatPhone = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

// ───── 공통 스타일 ─────

const TabBarWrap = styled.div`
  margin: -${({ theme }) => theme.realtor.pagePadding} -${({ theme }) => theme.realtor.pagePadding} 0;
  background: ${({ theme }) => theme.colors.primary};
  padding: 0 ${({ theme }) => theme.realtor.pagePadding} ${({ theme }) => theme.spacing.base};
  position: sticky;
  top: 0;
  z-index: 40;
`;

const TabBarHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: ${({ theme }) => theme.spacing.base};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const TabBarTitleArea = styled.div``;

const TabBarTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  margin-bottom: 2px;
`;

const TabBarDesc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: rgba(255, 255, 255, 0.85);
`;


const SectionLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

const TabRow = styled.div`
  display: flex;
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const TabLink = styled.button`
  flex: 1;
  text-align: center;
  padding: 10px 0;
  font-family: inherit;
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.pill};
  transition: all 0.15s;
  cursor: pointer;
  pointer-events: auto;
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
`;

const Content = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.realtor.sectionGap};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: #9CA3AF;
  font-family: 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.6;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

/* 폼 */
const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const EditToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const EditTextarea = styled.textarea`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.base};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  resize: none;
  min-height: 120px;
  outline: none;
  font-family: inherit;
  margin-bottom: ${({ theme }) => theme.spacing.base};
  box-sizing: border-box;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const BtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ActionBtn = styled.button`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md};
  border: none;
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  background: ${({ $variant, theme }) =>
    $variant === 'kakao' ? '#FEE500' : theme.colors.primary};
  color: ${({ $variant }) => ($variant === 'kakao' ? '#3C1E1E' : '#fff')};
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover { opacity: 0.9; }
`;

const QrBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  width: 100%;
  padding: ${({ theme }) => theme.spacing.md};
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;

  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

/* 상태 그리드 (회색 카드 + 흰 셀) */
const StatsCard = styled.div`
  background: #F1F5FF;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols || 3}, 1fr)`};
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StatCell = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 8px 4px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const StatLabelWrap = styled.div`
  height: 18px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StatLabel = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  line-height: 1.3;
`;

const StatSub = styled.span`
  font-size: 7px;
  color: ${({ theme }) => theme.colors.textLight};
  line-height: 1;
  white-space: nowrap;
`;

const StatValue = styled.p`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 2px;
`;

/* 정산금 현황 */
const SettlementCard = styled.div`
  background: linear-gradient(180deg, #036EB8 0%, #0A285F 100%);
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing.lg};
`;

const SettlementTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SettlementRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SettlementItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: ${({ theme }) => theme.spacing.md};
`;

const SettlementItemTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  white-space: nowrap;
`;

const SettlementItemLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
`;

const SettlementWithdrawBtn = styled.button`
  padding: 3px 10px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

const SettlementItemValue = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: #fff;
  text-align: right;
`;

/* 공지사항 */
const NoticeCard = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0 ${({ theme }) => theme.spacing.lg};
`;

const NoticeItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  cursor: pointer;

  &:last-child { border-bottom: none; }
`;

const NoticeLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const NoticeDate = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 2px;
`;

const NoticeTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
`;

const NoticeTag = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const NoticeArrow = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  flex-shrink: 0;
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

// ───── 컴포넌트 ─────
const isCustomerTab = (path) =>
  path === '/realtor/recommend' || path === '/realtor/recommend/customer';

const InviteCustomer = () => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPreview, setShowPreview] = useState(true);
  const [customerStats, setCustomerStats] = useState(DEFAULT_CUSTOMER_STATS);
  const [liveNotices, setLiveNotices] = useState([]);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { userData } = useAuth();
  const formUrl = buildFormUrl(userData?.uid);
  const orgName = userData?.businessName || userData?.business_name || userData?.name || '부동산';
  const [msgText, setMsgText] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [saving, setSaving] = useState(false);

  // 파트너 단체 메시지 발송 모달
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partnerList, setPartnerList] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [partnerMsgText, setPartnerMsgText] = useState('');
  const [sending, setSending] = useState(false);

  // 파트너 초대 현황 실시간 통계
  const [partnerStats, setPartnerStats] = useState(DEFAULT_PARTNER_STATS);

  const loadPartnerStats = async () => {
    if (!userData?.uid) return;
    const s = await getReferralStats(userData.uid);
    setPartnerStats([
      { label: '발송', value: s.sent },
      { label: '가입', value: s.joined },
      { label: '만료예정', sub: '(6개월 이내)', value: s.expiring },
      { label: '완료', value: s.expired },
    ]);
  };

  useEffect(() => { loadPartnerStats(); }, [userData?.uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let alive = true;
    (async () => {
      const mine = await getRealtorTemplate(userData?.uid);
      if (!alive) return;
      if (mine) {
        const rendered = renderTemplate(mine, { orgName, formUrl });
        setMsgText(rendered);
        setSavedMsg(rendered);
        return;
      }
      const hq = await getHqDefaultTemplate();
      const tmpl = hq || FALLBACK_TEMPLATE;
      const rendered = renderTemplate(tmpl, { orgName, formUrl });
      if (!alive) return;
      setMsgText(rendered);
      setSavedMsg(rendered);
    })();
    return () => { alive = false; };
  }, [userData?.uid, formUrl, orgName]);

  const handleSaveTemplate = async () => {
    if (!userData?.uid || msgText === savedMsg || saving) return;
    setSaving(true);
    try {
      // 편집 원문을 placeholder 로 되돌려 저장 (orgName/formUrl 재치환 대응)
      const templated = msgText
        .split(formUrl).join('{formUrl}')
        .split(`"${orgName}"`).join('"{orgName}"');
      await saveRealtorTemplate(userData.uid, templated);
      setSavedMsg(msgText);
    } catch (_) {
      // 무시 (네트워크 실패 시 로컬 편집값 유지)
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!userData?.uid) return;
    getRequestCountsByStatus(userData.uid).then((counts) => {
      setCustomerStats(DEFAULT_CUSTOMER_STATS.map((s) => ({ ...s, value: counts[s.key] || 0 })));
    }).catch(() => {});
  }, [userData?.uid]);

  useEffect(() => {
    getNotices(1, 3).then(setLiveNotices).catch(() => {});
  }, []);

  const handleSms = async () => {
    const rawPhone = phone.replace(/\D/g, '');
    if (!rawPhone) {
      alert('휴대폰 번호를 입력해주세요.');
      return;
    }
    await handleSaveTemplate();
    const body = encodeURIComponent(msgText);
    window.location.href = `sms:${rawPhone}?body=${body}`;
  };

  const handleKakao = async () => {
    await handleSaveTemplate();
    // msgText 안에 formUrl 이 이미 포함되어 있으므로 url 별도 전달하지 않음 (중복 방지)
    const shared = requestShare({
      title: '이음 - 이사 서비스 무료 견적',
      text: msgText,
    });
    if (!shared) {
      // 웹 브라우저(앱 아닌 환경)에서는 클립보드 복사
      navigator.clipboard.writeText(msgText).then(() => {
        alert('링크가 클립보드에 복사되었습니다.\n카카오톡에 붙여넣기 해주세요.');
      });
    }
  };

  // 파트너(내가 초대한 공인중개사) 목록 로드 후 모달 오픈
  const openPartnerMsgModal = async () => {
    setShowPartnerModal(true);
    setPartnerMsgText(msgText);
    setSelectedPartnerIds([]);
    if (!userData?.uid) return;
    setPartnerLoading(true);
    try {
      // 내가 추천한 파트너(realtors.referrer_id == 나)
      const snap = await getDocs(query(
        collection(db, 'realtors'),
        where('referrer_id', '==', userData.uid)
      ));
      const referred = snap.docs.map((d) => {
        const r = d.data();
        return {
          id: d.id,
          name: r.name || r.businessName || r.business_name || '이름없음',
          businessName: r.businessName || r.business_name || '',
          phone: (r.phone || '').replace(/\D/g, ''),
        };
      });
      // realtors 문서에 phone 이 없을 수 있어 users 에서 보강
      for (const p of referred) {
        if (p.phone.length < 10) {
          try {
            const uSnap = await getDocs(query(
              collection(db, 'users'),
              where('__name__', '==', p.id)
            ));
            const u = uSnap.docs[0]?.data();
            if (u?.phone) p.phone = u.phone.replace(/\D/g, '');
          } catch (_) {}
        }
      }
      const list = referred.filter((p) => p.phone.length >= 10);
      setPartnerList(list);
    } catch (_) {
      setPartnerList([]);
    } finally {
      setPartnerLoading(false);
    }
  };

  const toggleSelectPartner = (id) => {
    setSelectedPartnerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPartners = () => {
    if (selectedPartnerIds.length === partnerList.length) {
      setSelectedPartnerIds([]);
    } else {
      setSelectedPartnerIds(partnerList.map((p) => p.id));
    }
  };

  const handleSendPartnerSms = async () => {
    if (selectedPartnerIds.length === 0) {
      alert('발송할 파트너를 선택해주세요.');
      return;
    }
    if (!partnerMsgText.trim()) {
      alert('메시지 내용을 입력해주세요.');
      return;
    }
    if (!window.confirm(`선택한 ${selectedPartnerIds.length}명에게 문자를 발송합니다.\n계속할까요?`)) return;

    setSending(true);
    const targets = partnerList.filter((p) => selectedPartnerIds.includes(p.id));
    let ok = 0;
    let fail = 0;
    for (const p of targets) {
      try {
        const res = await fetch(SEND_SMS_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: p.phone, text: partnerMsgText }),
        });
        if (res.ok) {
          ok += 1;
          // 발송 로그 기록 (파트너 초대 현황 발송 카운트 +1)
          try {
            await addDoc(collection(db, 'realtor_invitations'), {
              realtor_id: userData.uid,
              invitee_id: p.id,
              invitee_name: p.name,
              invitee_phone: p.phone,
              channel: 'sms',
              message: partnerMsgText,
              created_at: serverTimestamp(),
            });
          } catch (_) {}
        } else {
          fail += 1;
        }
      } catch (_) {
        fail += 1;
      }
    }
    setSending(false);
    alert(`발송 완료\n성공: ${ok}건 / 실패: ${fail}건`);
    if (ok > 0) {
      setShowPartnerModal(false);
      loadPartnerStats();
    }
  };

  return (
    <div>
      {/* 파란 헤더 */}
      <TabBarWrap>
        <TabBarHeader>
          <TabBarTitleArea>
            <TabBarTitle>추천하기</TabBarTitle>
            <TabBarDesc>고객·동료 중개사에게 문자/카카오 발송</TabBarDesc>
          </TabBarTitleArea>

        </TabBarHeader>
      </TabBarWrap>

      {/* 3탭 */}
      <TabRow>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/customer')} $active={isCustomerTab(pathname)}>고객 초대</TabLink>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/realtor')} $active={pathname === '/realtor/recommend/realtor'}>공인중개사 초대</TabLink>
        <TabLink type="button" onClick={() => navigate('/realtor/recommend/qr')} $active={pathname === '/realtor/recommend/qr'}>전용 QR</TabLink>
      </TabRow>

      <Content>
        <RealtorInviteSection />

        {/* 파트너 초대 현황 */}
        <section>
          <StatsCard>
            <SectionTitle>파트너 초대 현황 <InfoTooltip text="동료 공인중개사 분들에게 이음을 추천해주세요. 해당 공인중개사님이 초청을 받아(추천인 코드가 입력된 상태로) 이음에 가입하면 그 공인중개사 마케팅 수익의 5%를 1년 동안 받으실 수 있습니다." /></SectionTitle>
            <StatsGrid $cols={4}>
              {partnerStats.map((s, i) => (
                <StatCell key={i}>
                  <StatLabelWrap>
                    <StatLabel>{s.label}</StatLabel>
                    {s.sub && <StatSub>{s.sub}</StatSub>}
                  </StatLabelWrap>
                  <StatValue>{s.value}</StatValue>
                </StatCell>
              ))}
            </StatsGrid>
          </StatsCard>
        </section>

        {/* 파트너 단체 메시지 발송 */}
        <section>
          <SectionTitle>파트너 단체 메시지 발송 <InfoTooltip text={`자동으로 고객에게 보낼 문구를 편집할 수 있습니다. 해당 문구 하단에는 ${userData?.businessName || userData?.business_name || userData?.name || '부동산'} 전용 신청링크가 함께 발송됩니다.`} /></SectionTitle>
          <MsgLinkList>
            <MsgLinkItem onClick={openPartnerMsgModal}>
              <span>SMS/LMS 메시지 보내기</span>
              <IoChevronForward size={16} color="#9CA3AF" />
            </MsgLinkItem>
            <MsgLinkItem onClick={() => alert('Kakao (준비중)')}>
              <span>Kakao 메시지 보내기</span>
              <IoChevronForward size={16} color="#9CA3AF" />
            </MsgLinkItem>
          </MsgLinkList>
        </section>

        {/* 공지사항 */}
        <section>
          <NoticeCard>
            <SectionTitle style={{ paddingTop: 20 }}>공지사항</SectionTitle>
            {(liveNotices.length > 0 ? liveNotices : DUMMY_NOTICES).map((n) => (
              <NoticeItem key={n.id} onClick={() => navigate('/realtor/mypage/notice')}>
                <NoticeLeft>
                  <NoticeDate>{n.date}</NoticeDate>
                  <NoticeTitle><NoticeTag>[{n.target || n.tag || '공지'}]</NoticeTag> {n.title}</NoticeTitle>
                </NoticeLeft>
                <NoticeArrow><IoChevronDown size={16} /></NoticeArrow>
              </NoticeItem>
            ))}
          </NoticeCard>
        </section>
      </Content>

      {/* 파트너 단체 메시지 발송 모달 */}
      {showPartnerModal && (
        <ModalOverlay onClick={() => !sending && setShowPartnerModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>파트너 단체 SMS/LMS 발송</ModalTitle>
              <ModalClose onClick={() => !sending && setShowPartnerModal(false)}>
                <IoClose size={20} />
              </ModalClose>
            </ModalHeader>

            <ModalSection>
              <ModalLabel>메시지 내용</ModalLabel>
              <ModalTextarea
                value={partnerMsgText}
                onChange={(e) => setPartnerMsgText(e.target.value)}
                placeholder="파트너에게 보낼 문구를 입력하세요"
              />
              <ModalHelper>{partnerMsgText.length}자</ModalHelper>
            </ModalSection>

            <ModalSection>
              <ModalLabel>
                수신 파트너 선택 ({selectedPartnerIds.length}/{partnerList.length})
                {partnerList.length > 0 && (
                  <SelectAllBtn onClick={toggleSelectAllPartners}>
                    {selectedPartnerIds.length === partnerList.length ? '전체해제' : '전체선택'}
                  </SelectAllBtn>
                )}
              </ModalLabel>
              {partnerLoading ? (
                <EmptyNote>불러오는 중...</EmptyNote>
              ) : partnerList.length === 0 ? (
                <EmptyNote>초대한 파트너가 없습니다.</EmptyNote>
              ) : (
                <PartnerList>
                  {partnerList.map((p) => (
                    <PartnerItem key={p.id} onClick={() => toggleSelectPartner(p.id)}>
                      <Checkbox $checked={selectedPartnerIds.includes(p.id)}>
                        {selectedPartnerIds.includes(p.id) && '✓'}
                      </Checkbox>
                      <PartnerInfo>
                        <PartnerName>
                          {p.name}
                          {p.businessName && <PartnerBiz> · {p.businessName}</PartnerBiz>}
                        </PartnerName>
                        <PartnerPhone>{p.phone.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')}</PartnerPhone>
                      </PartnerInfo>
                    </PartnerItem>
                  ))}
                </PartnerList>
              )}
            </ModalSection>

            <ModalFooter>
              <ModalBtn $variant="ghost" onClick={() => setShowPartnerModal(false)} disabled={sending}>
                취소
              </ModalBtn>
              <ModalBtn onClick={handleSendPartnerSms} disabled={sending}>
                {sending ? '발송중...' : `문자 발송 (${selectedPartnerIds.length}명)`}
              </ModalBtn>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}
    </div>
  );
};

const MsgLinkList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md};
`;

const MsgLinkItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.base} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  &:last-child { border-bottom: none; }
`;

/* 모달 */
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  background: #fff;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalTitle = styled.h3`
  font-size: 16px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const ModalClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  display: flex;
  align-items: center;
  padding: 4px;
`;

const ModalSection = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  overflow-y: auto;
  &:last-of-type { flex: 1; min-height: 0; }
`;

const ModalLabel = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SelectAllBtn = styled.button`
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.primary};
  cursor: pointer;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  outline: none;
  box-sizing: border-box;
  resize: none;
  font-family: inherit;
  line-height: 1.5;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ModalHelper = styled.div`
  text-align: right;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 4px;
`;

const PartnerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 280px;
  overflow-y: auto;
`;

const PartnerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.borderLight}; }
`;

const Checkbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid ${({ $checked, theme }) => ($checked ? theme.colors.primary : theme.colors.border)};
  background: ${({ $checked, theme }) => ($checked ? theme.colors.primary : '#fff')};
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PartnerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const PartnerName = styled.div`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const PartnerBiz = styled.span`
  font-weight: 400;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PartnerPhone = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 2px;
`;

const EmptyNote = styled.div`
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 20px 16px;
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  background: ${({ $variant, theme }) => ($variant === 'ghost' ? '#fff' : theme.colors.primary)};
  color: ${({ $variant, theme }) => ($variant === 'ghost' ? theme.colors.textSecondary : '#fff')};
  border: ${({ $variant, theme }) => ($variant === 'ghost' ? `1px solid ${theme.colors.border}` : 'none')};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export default InviteCustomer;
